import { useEffect, useState } from "react";
import { Button, DatePicker, Form, Modal, Select, Switch } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { ApprovalSubstitute, User } from "../../utils/types";
import {
  getErrorMessage,
  Notify,
  postData,
  updateData,
} from "../../utils/constants";

const { RangePicker } = DatePicker;

type OutOfOfficeModalProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  managers: User[];
  editing: ApprovalSubstitute | null;
  refreshData: () => void;
};

const OutOfOfficeModal = ({
  isOpen,
  setIsOpen,
  managers,
  editing,
  refreshData,
}: OutOfOfficeModalProps) => {
  const [substituteUserId, setSubstituteUserId] = useState<number | null>(
    null,
  );
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) return;

    if (editing) {
      setSubstituteUserId(editing.substituteUserId);
      setDateRange([dayjs(editing.startDate), dayjs(editing.endDate)]);
      setIsActive(editing.status === "ACTIVE");
    } else {
      setSubstituteUserId(null);
      setDateRange(null);
      setIsActive(true);
    }
  }, [isOpen, editing]);

  const handleSubmit = async () => {
    if (!substituteUserId || !dateRange) {
      Notify("error", "Please select a substitute and a date range.");
      return;
    }

    setIsSubmitting(true);
    const payload = {
      substituteUserId,
      startDate: `${dateRange[0].format("YYYY-MM-DD")}T00:00:00`,
      endDate: `${dateRange[1].format("YYYY-MM-DD")}T23:59:59`,
      status: isActive ? "ACTIVE" : "INACTIVE",
    };

    try {
      if (editing) {
        await updateData(
          `${process.env.REACT_APP_API_URL}/substitutes/${editing.id}`,
          payload,
        );
        Notify("success", "Out of Office period updated successfully!");
      } else {
        await postData(
          `${process.env.REACT_APP_API_URL}/substitutes`,
          payload,
        );
        Notify("success", "Out of Office period created successfully!");
      }
      refreshData();
      setIsOpen(false);
    } catch (error) {
      console.error("Out of Office save error:", error);
      Notify(
        "error",
        getErrorMessage(
          error,
          "Oops! Something went wrong while saving the Out of Office period.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={editing ? "Edit Out of Office period" : "New Out of Office period"}
      open={isOpen}
      onCancel={() => setIsOpen(false)}
      footer={[
        <Button key="cancel" onClick={() => setIsOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={isSubmitting}
        >
          Save
        </Button>,
      ]}
    >
      <Form layout="vertical">
        <Form.Item label="Substitute manager" required>
          <Select
            showSearch
            placeholder="Select a manager"
            value={substituteUserId ?? undefined}
            onChange={(value) => setSubstituteUserId(value)}
            optionFilterProp="label"
            options={managers.map((manager) => ({
              label: `${manager.firstName} ${manager.lastName}`,
              value: manager.id,
            }))}
          />
        </Form.Item>

        <Form.Item label="Out of Office period" required>
          <RangePicker
            className="w-full"
            value={dateRange}
            onChange={(values) => {
              if (values && values[0] && values[1]) {
                setDateRange([values[0], values[1]]);
              } else {
                setDateRange(null);
              }
            }}
          />
        </Form.Item>

        <Form.Item label="Status">
          <Switch
            checked={isActive}
            onChange={setIsActive}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default OutOfOfficeModal;
